// ===========================================
// Admin Teacher Time-Off Panel
// ===========================================
// Per-teacher management of away periods, shown alongside the weekly
// availability editor in the teacher edit dialog:
//   - list all periods (any status)
//   - "Set teacher away" directly (APPROVED immediately) with the
//     affected-lessons preview + availability warning
//   - approve / decline PENDING requests (same preview at approval)
//   - cancel a PENDING request or an APPROVED period
// Existing bookings inside an away range are FLAGGED here, never changed —
// the admin arranges cover / reschedules / opens a clinic week themselves.

import { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  List,
  ListItem,
  ListItemText,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { BeachAccess as TimeOffIcon } from '@mui/icons-material';
import {
  useTeacherUnavailability,
  useSetTeacherAway,
  useApproveTimeOff,
  useDeclineTimeOff,
  useCancelTimeOff,
  useAwayAffectedLessons,
} from '../../hooks/useTeacherUnavailability';
import { TeacherUnavailabilityPeriod } from '../../api/users.api';
import { todayPlusDaysString, formatUTCDate } from '../../utils/dateUtils';
import {
  TIME_OFF_STATUS_CHIP,
  timeOffRangeLabel,
  timeOffErrorMessage,
  timeOffTermWeekLabels,
} from '../../utils/timeOff';
import { useTerms } from '../../hooks/useAdmin';

// ===========================================
// AFFECTED-LESSONS PREVIEW
// ===========================================

export interface AffectedLessonsPreviewProps {
  teacherId: string;
  startDate: string; // YYYY-MM-DD
  endDate: string; // YYYY-MM-DD
}

/**
 * "You'll need to handle these" — the teacher's existing commitments inside
 * the prospective away range. Read-only; nothing is changed automatically.
 */
/** Same cap as the backend (MAX_PERIOD_DAYS) — never ask for a longer preview. */
const PREVIEW_MAX_DAYS = 366;

function previewRangeDays(startDate: string, endDate: string): number {
  return (Date.parse(`${endDate}T00:00:00Z`) - Date.parse(`${startDate}T00:00:00Z`)) / 86400000 + 1;
}

export function AffectedLessonsPreview({ teacherId, startDate, endDate }: AffectedLessonsPreviewProps) {
  const affectedMutation = useAwayAffectedLessons();
  const { mutate: fetchAffected } = affectedMutation;

  // Debounced: typing through a date field emits intermediate valid-looking
  // values (e.g. a half-edited year → a centuries-long range). Wait for the
  // input to settle and clamp to the server's own range cap before fetching —
  // the preview walks the range day-by-day server-side.
  useEffect(() => {
    if (!teacherId || !startDate || !endDate || endDate < startDate) return;
    const days = previewRangeDays(startDate, endDate);
    if (!Number.isFinite(days) || days < 1 || days > PREVIEW_MAX_DAYS) return;
    const timer = setTimeout(() => fetchAffected({ teacherId, startDate, endDate }), 300);
    return () => clearTimeout(timer);
  }, [teacherId, startDate, endDate, fetchAffected]);

  if (!startDate || !endDate || endDate < startDate) return null;
  const rangeDays = previewRangeDays(startDate, endDate);
  if (!Number.isFinite(rangeDays) || rangeDays < 1) return null;
  if (rangeDays > PREVIEW_MAX_DAYS) {
    return <Alert severity="warning">Range is too long to preview (max {PREVIEW_MAX_DAYS} days).</Alert>;
  }

  if (affectedMutation.isPending) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1 }}>
        <CircularProgress size={16} />
        <Typography variant="caption" color="text.secondary">
          Checking scheduled classes in this range…
        </Typography>
      </Box>
    );
  }

  if (affectedMutation.isError) {
    return <Alert severity="warning">Couldn't check scheduled classes: {timeOffErrorMessage(affectedMutation.error)}</Alert>;
  }

  const affected = affectedMutation.data ?? [];
  if (affected.length === 0) {
    return (
      <Alert severity="success" sx={{ '& .MuiAlert-message': { py: 0.5 } }}>
        No scheduled classes fall inside this range.
      </Alert>
    );
  }

  return (
    <Alert severity="warning">
      <Typography variant="body2" fontWeight={600} gutterBottom>
        {affected.length} scheduled {affected.length === 1 ? 'class falls' : 'classes fall'} inside this
        range — nothing is changed automatically. Arrange a replacement teacher, reschedule, or open
        the clinic week for each one.
      </Typography>
      <List dense disablePadding sx={{ maxHeight: 180, overflowY: 'auto' }}>
        {affected.map((item, idx) => (
          <ListItem key={`${item.date}-${item.startTime}-${idx}`} sx={{ px: 0, py: 0.25 }}>
            <ListItemText
              primary={
                <Typography variant="caption">
                  <strong>{formatUTCDate(item.date)}</strong> {item.startTime}–{item.endTime} · {item.label}
                </Typography>
              }
            />
          </ListItem>
        ))}
      </List>
    </Alert>
  );
}

// ===========================================
// SET-AWAY DIALOG (admin door — APPROVED immediately)
// ===========================================

interface SetTeacherAwayDialogProps {
  open: boolean;
  onClose: () => void;
  teacherId: string;
  teacherName: string;
}

function SetTeacherAwayDialog({ open, onClose, teacherId, teacherName }: SetTeacherAwayDialogProps) {
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [reason, setReason] = useState('');
  const setAwayMutation = useSetTeacherAway();

  const minDate = todayPlusDaysString(0);
  const rangeInvalid = !!startDate && !!endDate && endDate < startDate;
  const rangeReady = !!startDate && !!endDate && !rangeInvalid;

  const handleClose = () => {
    setAwayMutation.reset();
    setStartDate('');
    setEndDate('');
    setReason('');
    onClose();
  };

  const handleSubmit = () => {
    setAwayMutation.mutate(
      { teacherId, data: { startDate, endDate, reason: reason.trim() || undefined } },
      { onSuccess: handleClose }
    );
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Set {teacherName} as Away</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 0.5 }}>
          <Alert severity="info">
            This takes effect immediately — no new bookings (clinics, extras, make-ups, meet &
            greets) can be placed for {teacherName} on these days.
          </Alert>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField
              label="First day away"
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              fullWidth
              InputLabelProps={{ shrink: true }}
              inputProps={{ min: minDate }}
            />
            <TextField
              label="Last day away"
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              fullWidth
              InputLabelProps={{ shrink: true }}
              inputProps={{ min: startDate || minDate }}
              error={rangeInvalid}
              helperText={rangeInvalid ? 'Last day must be on or after the first day' : 'Both days are included'}
            />
          </Stack>
          <TextField
            label="Reason (optional)"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            fullWidth
            multiline
            rows={2}
            inputProps={{ maxLength: 500 }}
          />
          {rangeReady && (
            <AffectedLessonsPreview teacherId={teacherId} startDate={startDate} endDate={endDate} />
          )}
          {setAwayMutation.isError && (
            <Alert severity="error">{timeOffErrorMessage(setAwayMutation.error)}</Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={!rangeReady || setAwayMutation.isPending}
        >
          {setAwayMutation.isPending ? 'Saving…' : 'Set Away'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ===========================================
// REVIEW DIALOG (approve / decline a PENDING request)
// ===========================================

export interface ReviewTimeOffDialogProps {
  open: boolean;
  onClose: () => void;
  mode: 'approve' | 'decline';
  period: TeacherUnavailabilityPeriod | null;
  /** Falls back to period.teacherId; pass when the period row came from the inbox. */
  teacherName?: string;
}

export function ReviewTimeOffDialog({ open, onClose, mode, period, teacherName }: ReviewTimeOffDialogProps) {
  const [reviewNote, setReviewNote] = useState('');
  const approveMutation = useApproveTimeOff();
  const declineMutation = useDeclineTimeOff();
  const mutation = mode === 'approve' ? approveMutation : declineMutation;

  const handleClose = () => {
    approveMutation.reset();
    declineMutation.reset();
    setReviewNote('');
    onClose();
  };

  if (!period) return null;

  const startDate = period.startDate.slice(0, 10);
  const endDate = period.endDate.slice(0, 10);
  const displayName =
    teacherName ??
    (period.Teacher ? `${period.Teacher.User.firstName} ${period.Teacher.User.lastName}` : 'this teacher');

  const handleSubmit = () => {
    const args = {
      teacherId: period.teacherId,
      periodId: period.id,
      reviewNote: reviewNote.trim() || undefined,
    };
    if (mode === 'approve') {
      approveMutation.mutate(args, { onSuccess: handleClose });
    } else {
      declineMutation.mutate(args, { onSuccess: handleClose });
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {mode === 'approve' ? 'Approve' : 'Decline'} Time Off — {displayName}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 0.5 }}>
          <Typography variant="body2">
            <strong>{timeOffRangeLabel(period)}</strong>
            {period.reason ? ` — ${period.reason}` : ''}
          </Typography>
          {mode === 'approve' ? (
            <>
              <Alert severity="info">
                Approving blocks all new bookings for {displayName} on these days.
              </Alert>
              <AffectedLessonsPreview
                teacherId={period.teacherId}
                startDate={startDate}
                endDate={endDate}
              />
            </>
          ) : (
            <Typography variant="body2" color="text.secondary">
              The teacher will be notified that the request was declined.
            </Typography>
          )}
          <TextField
            label={mode === 'approve' ? 'Note to teacher (optional)' : 'Reason for declining (optional)'}
            value={reviewNote}
            onChange={(e) => setReviewNote(e.target.value)}
            fullWidth
            multiline
            rows={2}
            inputProps={{ maxLength: 500 }}
          />
          {mutation.isError && <Alert severity="error">{timeOffErrorMessage(mutation.error)}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <Button
          variant="contained"
          color={mode === 'approve' ? 'primary' : 'error'}
          onClick={handleSubmit}
          disabled={mutation.isPending}
        >
          {mutation.isPending ? 'Saving…' : mode === 'approve' ? 'Approve' : 'Decline'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ===========================================
// PANEL
// ===========================================

interface TeacherTimeOffPanelProps {
  teacherId: string;
  teacherName: string;
}

export default function TeacherTimeOffPanel({ teacherId, teacherName }: TeacherTimeOffPanelProps) {
  const { data: periods, isLoading } = useTeacherUnavailability(teacherId);
  const cancelMutation = useCancelTimeOff();
  const [setAwayOpen, setSetAwayOpen] = useState(false);
  const [review, setReview] = useState<{ mode: 'approve' | 'decline'; period: TeacherUnavailabilityPeriod } | null>(
    null
  );

  // Terms let each period row show which term week(s) the dates land on.
  const { data: terms } = useTerms();
  const list = periods ?? [];

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <TimeOffIcon color="primary" fontSize="small" />
          <Typography variant="h6">Time Off / Unavailability</Typography>
        </Box>
        <Button size="small" variant="outlined" onClick={() => setSetAwayOpen(true)}>
          Set Away
        </Button>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        Approved periods block all new bookings for this teacher (clinics, extras, make-ups, meet &
        greets). Existing classes in an away range are flagged for you to handle — they are never
        changed automatically.
      </Typography>

      {isLoading ? (
        <Box sx={{ py: 2, textAlign: 'center' }}>
          <CircularProgress size={24} />
        </Box>
      ) : list.length === 0 ? (
        <Paper variant="outlined" sx={{ p: 2, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            No time off recorded for {teacherName}.
          </Typography>
        </Paper>
      ) : (
        <Paper variant="outlined">
          <List dense disablePadding>
            {list.map((period) => {
              const chip = TIME_OFF_STATUS_CHIP[period.status];
              const actionable = period.status === 'PENDING' || period.status === 'APPROVED';
              return (
                <ListItem key={period.id} divider sx={{ py: 1 }}>
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                        <Typography variant="body2" fontWeight={500}>
                          {timeOffRangeLabel(period)}
                        </Typography>
                        <Chip
                          label={chip.label}
                          size="small"
                          sx={{ bgcolor: chip.bgcolor, color: chip.color, fontWeight: 600 }}
                        />
                        {timeOffTermWeekLabels(period, terms).map((label) => (
                          <Chip key={label} label={label} size="small" variant="outlined" />
                        ))}
                      </Box>
                    }
                    secondary={period.reason || undefined}
                  />
                  <Stack direction="row" spacing={0.5}>
                    {period.status === 'PENDING' && (
                      <>
                        <Button size="small" onClick={() => setReview({ mode: 'approve', period })}>
                          Approve
                        </Button>
                        <Button
                          size="small"
                          color="error"
                          onClick={() => setReview({ mode: 'decline', period })}
                        >
                          Decline
                        </Button>
                      </>
                    )}
                    {actionable && (
                      <Button
                        size="small"
                        color="inherit"
                        disabled={cancelMutation.isPending}
                        onClick={() => cancelMutation.mutate({ teacherId, periodId: period.id })}
                      >
                        Cancel
                      </Button>
                    )}
                  </Stack>
                </ListItem>
              );
            })}
          </List>
        </Paper>
      )}
      {cancelMutation.isError && (
        <Alert severity="error" sx={{ mt: 1 }}>
          {timeOffErrorMessage(cancelMutation.error)}
        </Alert>
      )}

      <SetTeacherAwayDialog
        open={setAwayOpen}
        onClose={() => setSetAwayOpen(false)}
        teacherId={teacherId}
        teacherName={teacherName}
      />
      <ReviewTimeOffDialog
        open={!!review}
        onClose={() => setReview(null)}
        mode={review?.mode ?? 'approve'}
        period={review?.period ?? null}
        teacherName={teacherName}
      />
    </Box>
  );
}
